import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
	Plus,
	Search,
	Download,
	FileText,
	MoreVertical,
	Eye,
	CheckCircle,
	XCircle,
} from 'lucide-react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

import { cn, downloadCSV } from '../../lib/utils'
import { useAuth } from '../../providers/AuthProvider'
import { useToast } from '../../components/ui/Toast'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent } from '../../components/ui/Card'
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from '../../components/ui/Table'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import {
	useTransactions,
	usePostTransaction,
	useVoidTransaction,
	type Transaction,
} from '../../hooks/useTransactions'

const INVOICE_TYPES: Record<number, { name: string; nameAr: string; color: string }> = {
	0: { name: 'Purchase', nameAr: 'شراء', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
	1: { name: 'Purchase Return', nameAr: 'إرجاع شراء', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
	2: { name: 'Issue', nameAr: 'صرف', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
	3: { name: 'Issue Return', nameAr: 'إرجاع صرف', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200' },
	4: { name: 'Transfer', nameAr: 'تحويل', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
	11: { name: 'Sale', nameAr: 'بيع', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
}

export default function TransactionsPage() {
	const { t, i18n } = useTranslation()
	const { hasPermission } = useAuth()
	const { toast } = useToast()
	const navigate = useNavigate()

	const [page, setPage] = useState(1)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined)
	const [statusFilter, setStatusFilter] = useState<'all' | 'posted' | 'unposted' | 'voided'>('all')

	const isRtl = i18n.dir() === 'rtl'

	const {
		data: transactionsData,
		isLoading,
		error,
	} = useTransactions({
		page,
		limit: 20,
		search: searchTerm || undefined,
		type: typeFilter,
		posted: statusFilter === 'posted' ? true : statusFilter === 'unposted' ? false : undefined,
		voided: statusFilter === 'voided' ? true : undefined,
	})

	const transactions = transactionsData?.data ?? []
	const pagination = transactionsData?.pagination

	const postTransactionMutation = usePostTransaction()
	const voidTransactionMutation = useVoidTransaction()

	const handlePostTransaction = async (transaction: Transaction) => {
		try {
			await postTransactionMutation.mutateAsync({ id: transaction.id })
			toast.success(t('transactions.messages.postSuccess', 'Transaction posted successfully'))
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleVoidTransaction = async (transaction: Transaction) => {
		const reason = prompt(t('transactions.voidReason', 'Enter void reason:'))
		if (!reason) return

		try {
			await voidTransactionMutation.mutateAsync({ id: transaction.id, data: { reason } })
			toast.success(t('transactions.messages.voidSuccess', 'Transaction voided successfully'))
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleViewTransaction = (transaction: Transaction) => {
		navigate(`/invoices/${transaction.id}`)
	}

	const handleExportCSV = useCallback(() => {
		const exportData = transactions.map((transaction) => ({
			[t('transactions.fields.invoiceNo', 'Invoice No')]: transaction.invoiceNo,
			[t('transactions.fields.type', 'Type')]: INVOICE_TYPES[transaction.invType]?.nameAr || transaction.typeName,
			[t('transactions.fields.date', 'Date')]: new Date(transaction.invoiceDate).toLocaleDateString(),
			[t('transactions.fields.outlet', 'Outlet')]: transaction.outlet?.fromName || transaction.outlet?.from || '-',
			[t('transactions.fields.amount', 'Amount')]: ((transaction.amounts?.netAmount ?? (transaction.amounts as any)?.net ?? 0)).toFixed(2),
			[t('transactions.fields.status', 'Status')]: transaction.status?.voided
				? t('transactions.status.voided', 'Voided')
				: transaction.status?.posted
					? t('transactions.status.posted', 'Posted')
					: t('transactions.status.draft', 'Draft'),
		}))

		downloadCSV(exportData, `transactions_${new Date().toISOString().split('T')[0]}`)
	}, [transactions, t])

	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleSearch = useCallback((value: string) => {
		setSearchInputValue(value)
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}
		searchTimeoutRef.current = setTimeout(() => {
			setSearchTerm(value)
			setPage(1)
		}, 300)
	}, [])

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center min-h-[400px]"
				role="status"
				aria-live="polite"
			>
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	if (error) {
		const errorMessage = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] text-center" role="alert">
				<FileText className="h-12 w-12 text-gray-400 mb-4" />
				<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
					{t('common.error', 'Error')}
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{errorMessage}</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('transactions.title', 'Transactions')}
				subtitle={t('transactions.subtitle', 'View and manage all transactions')}
				actions={
					hasPermission('sales:create') && (
						<Button
							onClick={() => navigate('/invoices/new')}
							leftIcon={<Plus className="h-4 w-4" />}
						>
							{t('transactions.newTransaction', 'New Transaction')}
						</Button>
					)
				}
			/>

			<Card>
				<CardContent className="p-0">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-col gap-4">
							<div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
								<div className="relative flex-1 max-w-md">
									<Input
										type="text"
										placeholder={t('transactions.searchPlaceholder', 'Search transactions...')}
										value={searchInputValue}
										onChange={(e) => handleSearch(e.target.value)}
										leftIcon={<Search className="h-4 w-4" />}
									/>
								</div>
								<div className="flex gap-2">
									<select
										value={typeFilter || ''}
										onChange={(e) => {
											setTypeFilter(e.target.value ? parseInt(e.target.value) : undefined)
											setPage(1)
										}}
										className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
									>
										<option value="">{t('transactions.allTypes', 'All Types')}</option>
										{Object.entries(INVOICE_TYPES).map(([type, info]) => (
											<option key={type} value={type}>
												{i18n.language === 'ar' ? info.nameAr : info.name}
											</option>
										))}
									</select>
									<select
										value={statusFilter}
										onChange={(e) => {
											setStatusFilter(e.target.value as any)
											setPage(1)
										}}
										className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
									>
										<option value="all">{t('transactions.allStatuses', 'All Statuses')}</option>
										<option value="posted">{t('transactions.status.posted', 'Posted')}</option>
										<option value="unposted">{t('transactions.status.draft', 'Draft')}</option>
										<option value="voided">{t('transactions.status.voided', 'Voided')}</option>
									</select>
									<Button variant="outline" onClick={handleExportCSV} leftIcon={<Download className="h-4 w-4" />}>
										{t('common.exportCSV', 'Export CSV')}
									</Button>
								</div>
							</div>
						</div>
					</div>

					{transactions.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<FileText className="h-12 w-12 text-gray-400 mb-4" />
							<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('transactions.noTransactions', 'No transactions found')}
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
								{t('transactions.noTransactionsDescription', 'Get started by creating your first transaction.')}
							</p>
						</div>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('transactions.fields.invoiceNo', 'Invoice No')}</TableHead>
										<TableHead>{t('transactions.fields.type', 'Type')}</TableHead>
										<TableHead>{t('transactions.fields.date', 'Date')}</TableHead>
										<TableHead>{t('transactions.fields.outlet', 'Outlet')}</TableHead>
										<TableHead>{t('transactions.fields.amount', 'Amount')}</TableHead>
										<TableHead>{t('transactions.fields.status', 'Status')}</TableHead>
										<TableHead className="ltr:text-right rtl:text-left">
											{t('common.actions', 'Actions')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{transactions.map((transaction) => {
										const typeInfo = INVOICE_TYPES[transaction.invType] || { name: transaction.typeName, nameAr: transaction.typeName, color: 'bg-gray-100 text-gray-800' }
										return (
											<TableRow key={transaction.id}>
												<TableCell className="font-medium font-mono">
													{transaction.invoiceNo}
												</TableCell>
												<TableCell>
													<span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', typeInfo.color)}>
														{i18n.language === 'ar' ? typeInfo.nameAr : typeInfo.name}
													</span>
												</TableCell>
												<TableCell>
													{new Date(transaction.invoiceDate).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}
												</TableCell>
												<TableCell>
													{transaction.outlet?.fromName || transaction.outlet?.from || '-'}
												</TableCell>
												<TableCell>
													{((transaction.amounts?.netAmount ?? (transaction.amounts as any)?.net ?? 0)).toLocaleString(
														i18n.language === 'ar' ? 'ar-EG' : 'en-US',
														{ minimumFractionDigits: 2 }
													)}
												</TableCell>
												<TableCell>
													{transaction.status?.voided ? (
														<span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
															<XCircle className="h-3 w-3" />
															{t('transactions.status.voided', 'Voided')}
														</span>
													) : transaction.status?.posted ? (
														<span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
															<CheckCircle className="h-3 w-3" />
															{t('transactions.status.posted', 'Posted')}
														</span>
													) : (
														<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
															{t('transactions.status.draft', 'Draft')}
														</span>
													)}
												</TableCell>
												<TableCell className="ltr:text-right rtl:text-left">
													<Menu as="div" className="relative inline-block text-left">
														<MenuButton
															as={Button}
															variant="ghost"
															size="icon"
															className="h-8 w-8"
														>
															<span className="sr-only">{t('common.actions', 'Actions')}</span>
															<MoreVertical className="h-4 w-4" />
														</MenuButton>
														<MenuItems
															className={cn(
																'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
																isRtl ? 'left-0' : 'right-0'
															)}
														>
															<div className="py-1">
																<MenuItem>
																	{({ focus }) => (
																		<button
																			onClick={() => handleViewTransaction(transaction)}
																			className={cn(
																				'w-full flex items-center gap-2 px-4 py-2 text-sm',
																				focus
																					? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
																					: 'text-gray-700 dark:text-gray-300'
																			)}
																		>
																			<Eye className="h-4 w-4" />
																			{t('common.view', 'View')}
																		</button>
																	)}
																</MenuItem>
																{!transaction.status?.posted && !transaction.status?.voided && hasPermission('sales:update') && (
																	<MenuItem>
																		{({ focus }) => (
																			<button
																				onClick={() => handlePostTransaction(transaction)}
																				className={cn(
																					'w-full flex items-center gap-2 px-4 py-2 text-sm',
																					focus
																						? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
																						: 'text-gray-700 dark:text-gray-300'
																				)}
																			>
																				<CheckCircle className="h-4 w-4" />
																				{t('transactions.post', 'Post')}
																			</button>
																		)}
																	</MenuItem>
																)}
																{!transaction.status?.voided && hasPermission('sales:void') && (
																	<MenuItem>
																		{({ focus }) => (
																			<button
																				onClick={() => handleVoidTransaction(transaction)}
																				className={cn(
																					'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
																					focus && 'bg-red-50 dark:bg-red-900/20'
																				)}
																			>
																				<XCircle className="h-4 w-4" />
																				{t('transactions.void', 'Void')}
																			</button>
																		)}
																	</MenuItem>
																)}
															</div>
														</MenuItems>
													</Menu>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>

							{pagination && pagination.totalPages > 1 && (
								<nav
									className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
									aria-label={t('pagination.navigation', 'Pagination navigation')}
								>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										{t('common.showing', 'Showing')} {(page - 1) * 20 + 1}-
										{Math.min(page * 20, pagination.total)} {t('common.of', 'of')} {pagination.total}
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											{t('common.previous', 'Previous')}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
											disabled={page === pagination.totalPages}
										>
											{t('common.next', 'Next')}
										</Button>
									</div>
								</nav>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
